/**
 * C1 — Zone helpers for the Prompt Studio (lot A step 15).
 *
 * Single definitions, imported by lots B, C, D, E, F, G and the launch planner.
 * Nobody re-derives any of these.
 *
 * The FROZEN `promptPlaceKey` (prefix `p1:`) is defined here and never changed.
 * Any change to the prefix, part order, normalization or emptying rule is a `p2:` migration.
 */

import type { GeoZoneType, TargetZone } from '../types/targets';
import type { Location } from '../types/brand';

// ---------------------------------------------------------------------------
// validateZoneMeta (C1, lot C step 1)
// ---------------------------------------------------------------------------

const _GEO_ZONE_TYPES: readonly GeoZoneType[] = [
  'country', 'region', 'city', 'village', 'locality', 'postcode', 'address',
];

/** Mirrors `zoneKeyFrom` in conversation.ts for validation (NEVER stored, only checked). */
function _computeZoneKey(z: { type: GeoZoneType; countryCode: string; cityName?: string; regionName?: string; label: string }): string {
  if (z.type === 'country') return z.countryCode.toUpperCase();
  const raw = z.cityName ?? z.regionName ?? z.label;
  const slug = raw
    .toLowerCase()
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 24);
  return `${z.countryCode.toUpperCase()}-${slug}`;
}

/**
 * C1, lot C — Validate a client-supplied zone and return a clean `TargetZone`.
 *
 * Throws a plain Error with a French message; callers map it to 400.
 * Same rules as `conversations.service.ts validateAndDeriveZone` MINUS the lat/lng requirement.
 *
 *   - `countryCode` must match /^[A-Z]{2}$/
 *   - `type` must be one of `GeoZoneType`
 *   - `label` must be 1..80 chars
 *   - `latitude` / `longitude` must be finite numbers when present
 *   - `zoneKeyFrom(z)` must match /^[A-Z]{2}(-[a-z0-9-]{1,24})?$/
 *
 * Returns a NEW object holding only `TargetZone` keys (a GeoSuggestion is accepted
 * as input); `geocodeStatus = 'ok'` when both coordinates are present, else 'pending'
 * (client value ignored).
 *
 * Known limit: a non-country name with no Latin letters or digits (« 東京 ») slugs to
 * empty → `JP-` → 400. Accepted (Geoapify returns Latin `cityName`); never invent a
 * slug or fall back to the country.
 */
export function validateZoneMeta(z: unknown): TargetZone {
  if (!z || typeof z !== 'object') {
    throw new Error('La zone est invalide (objet attendu).');
  }
  const raw = z as Record<string, unknown>;

  // countryCode /^[A-Z]{2}$/
  const cc = typeof raw.countryCode === 'string' ? raw.countryCode : '';
  if (!/^[A-Z]{2}$/.test(cc)) {
    throw new Error(`Le code pays est invalide (2 lettres majuscules, ex. « FR »): ${JSON.stringify(raw.countryCode)}`);
  }

  // type ∈ GeoZoneType
  const type = typeof raw.type === 'string' ? raw.type : '';
  if (!_GEO_ZONE_TYPES.includes(type as GeoZoneType)) {
    throw new Error(`Le type de zone est invalide: ${JSON.stringify(type)}`);
  }

  // label 1..80 chars
  const label = typeof raw.label === 'string' ? raw.label : '';
  if (label.length < 1 || label.length > 80) {
    throw new Error('Le nom de la zone doit faire entre 1 et 80 caractères.');
  }

  // latitude/longitude finite numbers when present
  const hasLat = raw.latitude !== undefined && raw.latitude !== null;
  const hasLng = raw.longitude !== undefined && raw.longitude !== null;
  if (hasLat && (typeof raw.latitude !== 'number' || !isFinite(raw.latitude as number))) {
    throw new Error('La latitude doit être un nombre fini.');
  }
  if (hasLng && (typeof raw.longitude !== 'number' || !isFinite(raw.longitude as number))) {
    throw new Error('La longitude doit être un nombre fini.');
  }

  // Build the clean TargetZone (only TargetZone keys, client geocodeStatus ignored)
  const out: TargetZone = {
    label,
    type: type as GeoZoneType,
    countryCode: cc,
    geocodeStatus: (hasLat && hasLng) ? 'ok' : 'pending',
  };
  if (typeof raw.cityName === 'string' && raw.cityName) out.cityName = raw.cityName;
  if (typeof raw.regionName === 'string' && raw.regionName) out.regionName = raw.regionName;
  if (typeof raw.postcode === 'string' && raw.postcode) out.postcode = raw.postcode;
  if (typeof raw.address === 'string' && raw.address) out.address = raw.address;
  if (hasLat) out.latitude = raw.latitude as number;
  if (hasLng) out.longitude = raw.longitude as number;
  // providerPlaceId deliberately omitted (client-supplied, never stored)

  // zoneKeyFrom(out) must match /^[A-Z]{2}(-[a-z0-9-]{1,24})?$/
  const key = _computeZoneKey(out);
  if (!/^[A-Z]{2}(-[a-z0-9-]{1,24})?$/.test(key)) {
    throw new Error(`La zone ne produit pas de clé valide (nom sans caractères ASCII ?): ${JSON.stringify(label)}`);
  }

  return out;
}

// ---------------------------------------------------------------------------
// firstSegment / zoneLabel
// ---------------------------------------------------------------------------

/**
 * `firstSegment(x)` = `x` up to its first comma, trimmed.
 * « Paris, Île-de-France, France » → « Paris »
 */
export function firstSegment(x: string): string {
  const comma = x.indexOf(',');
  return (comma === -1 ? x : x.slice(0, comma)).trim();
}

/**
 * C1 derived legacy `zone` name from a `TargetZone` (written to the `zone` field
 * on PATCH when `zoneMeta` is stored, so old readers keep working).
 *
 *   region / locality / village → `firstSegment(z.label)` (geocoder puts the name there)
 *   else                        → `cityName ?? regionName ?? label` (short name)
 *
 * Never the full label.  Never Mondial (absent = Mondial, no sentinel).
 */
export function zoneLabel(z: TargetZone): string {
  if (z.type === 'region' || z.type === 'locality' || z.type === 'village') {
    return firstSegment(z.label);
  }
  return z.cityName ?? z.regionName ?? z.label;
}

/**
 * DISPLAY name of a zone (owner, 2026-10-30): an address reads « 12 Rue de Rivoli (Paris) »,
 * since `zoneLabel` alone gives its city and the street alone would not say which city. Every
 * other type = `zoneLabel`. Never stored and never sent to an engine: the stored `zone` stays
 * `zoneLabel` (C1), so changing this changes no key, no row and no measurement.
 */
export function zoneDisplayLabel(z: TargetZone): string {
  if (z.type !== 'address') return zoneLabel(z);
  const street = z.address || firstSegment(z.label);
  if (!street || !z.cityName || street === z.cityName) return zoneLabel(z);
  return `${street} (${z.cityName})`;
}

// ---------------------------------------------------------------------------
// promptPlaceKey — FROZEN (C1 R1)
// ---------------------------------------------------------------------------

const _RE_STRIP = /[̀-ͯ]/g;
const _RE_PIPE = /[|\u001f]/g;

function _part(x: string | undefined): string {
  if (!x) return '';
  return x
    .normalize('NFD')
    .replace(_RE_STRIP, '')  // strip diacritics
    .replace(_RE_PIPE, '')   // strip pipe + US
    .trim()
    .toLowerCase();
}

/**
 * C1 — THE one prompt place key.  FROZEN (prefix `p1:`).
 *
 * Format: `p1:<cc>|<type>|<address>|<postcode>|<city>|<region>|<name>`
 *
 * Rules:
 * - Parts finer than the zone's `type` are emptied.
 * - `name` = `firstSegment(label)` for region / locality / village, or when the
 *   type's own part (address / postcode / city / region / cc) is empty; else `''`.
 * - Never truncated; never slugged.
 *
 * Any change to the prefix, part order, normalization or the emptying rule
 * re-keys every stored identity → it is a `p2:` with a migration, never an edit.
 *
 * Golden fixtures (pinned by zoneMeta.spec.ts and lot E Python tests):
 *   {country, FR}                              → p1:fr|country|||||
 *   {city, FR, Paris, Île-de-France}           → p1:fr|city|||paris|ile-de-france|
 *   {postcode, FR, 75011, Paris, Île-de-France}→ p1:fr|postcode||75011|paris|ile-de-france|
 *   {region, FR, regionName Occitanie, label 'Haute-Garonne, Occitanie, France'}
 *                                              → p1:fr|region||||occitanie|haute-garonne
 *   {locality, FR, cityName Paris, label 'Belleville, Paris, …'}
 *                                              → p1:fr|locality|||paris|ile-de-france|belleville
 */
export function promptPlaceKey(z: TargetZone): string {
  const cc      = _part(z.countryCode);
  const type    = _part(z.type);
  const address = z.type === 'address' ? _part(z.address) : '';
  const postcode = (z.type === 'address' || z.type === 'postcode') ? _part(z.postcode) : '';
  const city    = (z.type !== 'country' && z.type !== 'region') ? _part(z.cityName) : '';
  const region  = z.type !== 'country' ? _part(z.regionName) : '';

  // `name` = first segment of label for region / locality / village, OR when the
  // type's own part is empty (so a département without a regionName still differs
  // from its parent region).
  let name = '';
  const typeOwn = address || postcode || city || region || cc;
  if (z.type === 'region' || z.type === 'locality' || z.type === 'village' || !typeOwn) {
    name = _part(firstSegment(z.label));
  }

  return `p1:${cc}|${type}|${address}|${postcode}|${city}|${region}|${name}`;
}

// ---------------------------------------------------------------------------
// storedPlaceKey — persisted identity, never reads brand.locations
// ---------------------------------------------------------------------------

/**
 * C1 — the stored place key for a prompt row.
 *
 * Used for EVERY persisted key or identity (E result-row zone part, E duplicate
 * guard, series identity).  Never reads `brand.locations`, so renaming or
 * deleting a Location never changes a stored key.
 *
 *   q.zoneMeta present    → `promptPlaceKey(q.zoneMeta)`
 *   q.zone set (legacy)   → `legacy:<trim+casefold q.zone>` (U+001F stripped)
 *   neither               → `'∅'`  (Mondial)
 */
export function storedPlaceKey(q: { zoneMeta?: TargetZone; zone?: string }): string {
  if (q.zoneMeta) return promptPlaceKey(q.zoneMeta);
  if (q.zone) {
    return 'legacy:' + q.zone.replace(/\u001f/g, '').trim().toLowerCase();
  }
  return '∅';
}

// ---------------------------------------------------------------------------
// zoneMetaFromLocation — Location → TargetZone (best-effort, no geocoder)
// ---------------------------------------------------------------------------

/**
 * C1 (lot A step 15 mapping) — derive a `TargetZone` from a brand `Location`, typed by
 * its `scale`: `pays` → country, `region` → region + `regionName`, `ville` → city and
 * `village` → village + `cityName`. No coordinates (a `Location` has none).
 * `null` when the country code is not 2 letters (never a country from a name) or the
 * scale is absent / `europe` / `monde`. The place's own name goes where the geocoder
 * puts its PARENT (the accepted Location-derived key split, C1).
 */
export function zoneMetaFromLocation(loc: Location): TargetZone | null {
  const countryCode = loc.countryCode?.trim().toUpperCase() ?? '';
  if (!/^[A-Z]{2}$/.test(countryCode)) return null;
  // `zone || label` for the name too: an empty `zone` must not key every such Location alike.
  const name = loc.zone || loc.label;
  const base = { label: name, countryCode, geocodeStatus: 'pending' as const };
  switch (loc.scale) {
    case 'pays':    return { ...base, type: 'country' };
    case 'region':  return { ...base, type: 'region', regionName: name };
    case 'ville':   return { ...base, type: 'city', cityName: name };
    case 'village': return { ...base, type: 'village', cityName: name };
    default:        return null;
  }
}

// ---------------------------------------------------------------------------
// effectiveZoneMeta — read-time, NEVER written
// ---------------------------------------------------------------------------

/**
 * C1 — compute the effective zone for display and dispatch.
 *
 *   q.zoneMeta present → q.zoneMeta (stored zone)
 *   q.zone set → the TargetZone derived from the brand Location whose
 *     `(zone || label)` matches q.zone (trim + casefold, the planner.py:146 rule;
 *     Location has no coordinates so none are set); or null if no match.
 *   neither → null (Mondial)
 *
 * `null` with q.zone set = UNRESOLVED LEGACY ZONE, never Mondial.
 * `null` with no q.zone = Mondial.
 *
 * Every zone reader (UI, coverage, duplicate rule, dispatch payload) uses this
 * one helper — never re-derives the rule.
 */
export function effectiveZoneMeta(
  q: { zoneMeta?: TargetZone; zone?: string },
  locations: readonly Location[] | undefined,
): TargetZone | null {
  if (q.zoneMeta) return q.zoneMeta;
  if (!q.zone) return null; // Mondial

  // Attempt to match the legacy zone string against a brand Location
  const needle = q.zone.trim().toLowerCase();
  const match = (locations ?? []).find(
    (l) => (l.zone || l.label).trim().toLowerCase() === needle,
  );
  if (!match) return null; // unresolved legacy zone
  return zoneMetaFromLocation(match);
}

// ---------------------------------------------------------------------------
// placeKey — display-only, reads brand.locations
// ---------------------------------------------------------------------------

/**
 * C1 — display-only place key (for Couverture columns, family place list, etc.).
 *
 * Never used for dispatch, identity, C7 or R3 inputs.
 *
 *   effective zone set → `promptPlaceKey(effective)`
 *   q.zone set (unresolved) → `legacy:<trim+casefold q.zone>`
 *   neither → `'∅'`  (Mondial)
 */
export function placeKey(
  q: { zoneMeta?: TargetZone; zone?: string },
  locations: readonly Location[] | undefined,
): string {
  const effective = effectiveZoneMeta(q, locations);
  if (effective) return promptPlaceKey(effective);
  if (q.zone) return 'legacy:' + q.zone.replace(/\u001f/g, '').trim().toLowerCase();
  return '∅';
}

// ---------------------------------------------------------------------------
// coveragePlaceKey — R19, Couverture display only
// ---------------------------------------------------------------------------

type ZoneSource = { zoneMeta?: TargetZone; zone?: string };

/**
 * C1 R19 — Couverture display-only key for a row WITHOUT stored `zoneMeta`.
 *
 * When the row's effective zone was derived from a brand Location (a legacy row),
 * this helper tries to find the ONE known zone that matches it, so the Couverture
 * column merges the Location-derived key with its geocoded twin.
 *
 * Matching rules:
 *   - A city with no regionName → equal `zoneKeyFrom` key  (same slug)
 *   - A region or village → equal (cc, type, `name` part)
 *
 * Only when exactly ONE known zone satisfies the rule; otherwise returns the
 * row's own `placeKey`.
 *
 * DISPLAY ONLY — never a dispatch or identity input, never R3 input.
 */
export function coveragePlaceKey(
  q: ZoneSource,
  locations: readonly Location[] | undefined,
  known: readonly TargetZone[],
): string {
  // Only apply to rows WITHOUT stored zoneMeta
  if (q.zoneMeta) return placeKey(q, locations);

  const effective = effectiveZoneMeta(q, locations);
  if (!effective) return placeKey(q, locations);

  // Attempt the R19 merge: find ONE known zone that matches the effective zone
  const nameOf = (z: TargetZone) => _part(firstSegment(z.label));

  let candidates: TargetZone[];
  if (effective.type === 'city' && !effective.regionName) {
    // City with no region → match by equal zoneKeyFrom slug
    const needle = _slugZone(effective);
    candidates = known.filter((z) => _slugZone(z) === needle);
  } else if (effective.type === 'region' || effective.type === 'village') {
    const cc = _part(effective.countryCode);
    const name = nameOf(effective);
    candidates = known.filter(
      (z) => z.type === effective.type && _part(z.countryCode) === cc && nameOf(z) === name,
    );
  } else {
    candidates = [];
  }

  if (candidates.length === 1) {
    return promptPlaceKey(candidates[0]);
  }
  return placeKey(q, locations);
}

/** Mirrors `zoneKeyFrom` slug logic for the city-level match (C1 R19). */
function _slugZone(z: TargetZone): string {
  const base = z.cityName ?? z.regionName ?? z.label;
  return (z.countryCode?.toLowerCase() ?? '') + '-' + base.toLowerCase().replace(/[^a-z0-9]/g, '').slice(0, 24);
}
